import { Chip } from "components/DesignSystem/Chips/Chips";
import { useWindowSize } from "hooks/useWindowSize";
import {
  Combobox,
  ComboboxProps,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { Filter } from "components/DesignSystem/Filter/Filter";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import { SourceFilter } from "components/InvoiceFilter/InvoiceFilter";
import { ACCOUNTING_METHODS, monthPeriod } from "constants/bookkeeping";
import { YYYY_MM_DD } from "constants/date";
import { lastFiveYears, PERIOD } from "constants/revenueMetrics";
import dayjs from "dayjs";
import { Form, Formik, useFormikContext } from "formik";
import { MultiValue, SingleValue } from "react-select";
import { formatDateByViewType } from "utils/formatDate";
import { FilterValues as RecurringFilterValues } from "./AnnualisedRecurringRevenue";
import { FilterValues as TotalFilterValues } from "./TotalRevenue";

const YearPicker = ({
  updateFilter,
  ...props
}: ComboboxProps & {
  updateFilter: <S extends "START_DATE" | "END_DATE">(
    name: S,
    newValue: {
      START_DATE: string;
      END_DATE: string;
    }[S]
  ) => void;
}) => {
  const { setFieldValue } = useFormikContext();

  const handleChange = (
    values: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    const { value } = (values as SingleValue<OptionData>) || {};

    const formatedDate =
      props.name === "START_DATE"
        ? dayjs(value).startOf("year").format(YYYY_MM_DD)
        : dayjs(value).endOf("year").format(YYYY_MM_DD);

    const date = value ? formatedDate : "";

    if (props.name) {
      setFieldValue(props.name, date);
      updateFilter(
        props.name as "START_DATE" | "END_DATE",
        dayjs(date).format(YYYY_MM_DD)
      );
    }
  };

  return (
    <Combobox
      onChange={handleChange}
      components={{ ClearIndicator: () => null }}
      placeholder="Select year"
      menuPortalTarget={document.body}
      {...props}
    />
  );
};

const YearSelectorForm = ({
  updateFilter,
  values,
}: {
  updateFilter: <S extends "START_DATE" | "END_DATE">(
    name: S,
    newValue: {
      START_DATE: string;
      END_DATE: string;
    }[S]
  ) => void;
  values: {
    START_DATE: string;
    END_DATE: string;
  };
}) => {
  const startFromlastFiveYears = lastFiveYears.map((year) => {
    return {
      label: year,
      value: year,
      isDisabled: year > dayjs(values?.END_DATE).format("YYYY"),
    };
  });

  const endToLastFiveYears = lastFiveYears.map((year) => {
    return {
      label: year,
      value: year,
      isDisabled: year < dayjs(values?.START_DATE).format("YYYY"),
    };
  });

  const initialDates = {
    START_DATE: startFromlastFiveYears.find(
      ({ value }) => value === dayjs(values?.START_DATE).format("YYYY")
    ),
    END_DATE: endToLastFiveYears.find(
      ({ value }) => value === dayjs(values?.END_DATE).format("YYYY")
    ),
  };

  return (
    <Formik initialValues={initialDates} onSubmit={() => {}}>
      <Form className="t-flex t-flex-col t-gap-4">
        <YearPicker
          updateFilter={updateFilter}
          withForm
          name="START_DATE"
          label="From"
          value={initialDates.START_DATE}
          options={startFromlastFiveYears}
        />
        <YearPicker
          updateFilter={updateFilter}
          withForm
          name="END_DATE"
          label="To"
          value={initialDates.END_DATE}
          options={endToLastFiveYears}
        />
      </Form>
    </Formik>
  );
};

// On phones the chips are hidden, so the filter button shows just the applied count.
const getMobileFilterTitle = (count: number) =>
  count > 0 ? `(${count})` : null;

export const RecurringRevenueFilter = ({
  filterValues,
  updateFilter,
}: {
  filterValues: RecurringFilterValues;
  updateFilter: <S extends keyof RecurringFilterValues>(
    name: S,
    newValue: RecurringFilterValues[S]
  ) => void;
}) => {
  const { isMobile } = useWindowSize();
  const appliedFilterCount =
    filterValues?.START_DATE || filterValues?.END_DATE ? 1 : 0;

  return (
    <Filter.Root
      defaultValue="VIEW_BY"
      title={isMobile ? getMobileFilterTitle(appliedFilterCount) : undefined}
      hideDivider={isMobile}
      capsule={
        <span className="t-contents sm:t-hidden">
          {(filterValues?.START_DATE || filterValues?.END_DATE) && (
            <Chip
              onRemove={() => {}}
              isRemovable={false}
              isActive
              filterType="VIEW_BY"
            >
              {formatDateByViewType(filterValues.START_DATE, PERIOD.MONTHLY)}
              {" to "}
              {formatDateByViewType(filterValues.END_DATE, PERIOD.MONTHLY)}
            </Chip>
          )}
        </span>
      }
    >
      <Filter.Portal>
        <Filter.List>
          <Filter.ListItem value="VIEW_BY">View by</Filter.ListItem>
        </Filter.List>
        <Filter.Body value="VIEW_BY" block>
          <BooksDateFilter
            values={filterValues}
            updateFilter={updateFilter}
            datePeriod={monthPeriod}
            pickerType="month"
          />
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};

export const TotalRevenueFilter = ({
  filterValues,
  updateFilter,
}: {
  filterValues: TotalFilterValues;
  updateFilter: <S extends keyof TotalFilterValues>(
    name: S,
    newValue: TotalFilterValues[S]
  ) => void;
}) => {
  const { isMobile } = useWindowSize();
  const appliedFilterCount = [
    filterValues.START_DATE || filterValues.END_DATE,
    filterValues.ACCOUNTING_METHOD,
    filterValues.SOURCE && filterValues.SOURCE.length > 0,
  ].filter(Boolean).length;

  return (
    <Filter.Root
      defaultValue="VIEW_BY"
      title={isMobile ? getMobileFilterTitle(appliedFilterCount) : undefined}
      hideDivider={isMobile}
      capsule={
        <span className="t-contents sm:t-hidden">
          {(filterValues.START_DATE || filterValues.END_DATE) && (
            <Chip
              onRemove={() => {}}
              isRemovable={false}
              isActive
              filterType="VIEW_BY"
            >
              {formatDateByViewType(
                filterValues.START_DATE,
                PERIOD[filterValues.VIEW_TYPE]
              )}
              {" to "}
              {formatDateByViewType(
                filterValues.END_DATE,
                PERIOD[filterValues.VIEW_TYPE]
              )}
            </Chip>
          )}
          {filterValues.ACCOUNTING_METHOD && (
            <Chip
              onRemove={() => {}}
              isRemovable={false}
              isActive
              filterType="ACCOUNTING_METHOD"
            >
              <div className="first-letter:t-uppercase t-lowercase">
                {filterValues.ACCOUNTING_METHOD}
              </div>
            </Chip>
          )}
          {filterValues.SOURCE && filterValues.SOURCE.length > 0 && (
            <Chip
              onRemove={() => updateFilter("SOURCE", [])}
              isRemovable={true}
              isActive
              filterType="SOURCE"
            >
              {`Sources (${filterValues.SOURCE.length})`}
            </Chip>
          )}
        </span>
      }
    >
      <Filter.Portal>
        <Filter.List>
          <Filter.ListItem value="VIEW_BY">View by</Filter.ListItem>
          <Filter.ListItem value="ACCOUNTING_METHOD">
            Accounting method
          </Filter.ListItem>
          <Filter.ListItem value="SOURCE">Source</Filter.ListItem>
        </Filter.List>
        <Filter.Body value="VIEW_BY" block>
          <RadioGroup.Root
            className="t-mb-6"
            onValueChange={(value: typeof filterValues.VIEW_TYPE) => {
              updateFilter("VIEW_TYPE", value);
            }}
            value={filterValues.VIEW_TYPE}
          >
            <RadioGroup.Content>
              <RadioGroup.Item value={PERIOD.MONTHLY}>Months</RadioGroup.Item>
              {filterValues.VIEW_TYPE === PERIOD.MONTHLY && (
                <BooksDateFilter
                  values={filterValues}
                  updateFilter={updateFilter}
                  datePeriod={monthPeriod}
                  pickerType="month"
                />
              )}

              <RadioGroup.Item value={PERIOD.QUARTERLY}>
                Quarters
              </RadioGroup.Item>
              {filterValues.VIEW_TYPE === PERIOD.QUARTERLY && (
                <BooksDateFilter
                  values={filterValues}
                  updateFilter={updateFilter}
                  datePeriod={monthPeriod}
                  pickerType="quarter"
                  showPeriodSelector={false}
                />
              )}
              <RadioGroup.Item value={PERIOD.YEARLY}>Years</RadioGroup.Item>
              {filterValues.VIEW_TYPE === PERIOD.YEARLY && (
                <YearSelectorForm
                  values={filterValues}
                  updateFilter={updateFilter}
                />
              )}
            </RadioGroup.Content>
          </RadioGroup.Root>
        </Filter.Body>
        <Filter.Body value="SOURCE">
          <SourceFilter
            filter={filterValues}
            updateFilter={(_, value) => updateFilter("SOURCE", value)}
          />
        </Filter.Body>
        <Filter.Body value="ACCOUNTING_METHOD" block>
          <RadioGroup.Root
            className="t-mb-6"
            onValueChange={(value: typeof filterValues.ACCOUNTING_METHOD) => {
              updateFilter("ACCOUNTING_METHOD", value);
            }}
            value={filterValues.ACCOUNTING_METHOD}
          >
            <RadioGroup.Content>
              <RadioGroup.Item value={ACCOUNTING_METHODS.CASH}>
                Cash
              </RadioGroup.Item>
              <RadioGroup.Item value={ACCOUNTING_METHODS.ACCRUAL}>
                Accrual
              </RadioGroup.Item>
            </RadioGroup.Content>
          </RadioGroup.Root>
        </Filter.Body>
      </Filter.Portal>
    </Filter.Root>
  );
};
